import React, { useState, useEffect } from 'react';
import { FaHandshake, FaNewspaper, FaLayerGroup, FaStore, FaUser, FaStar } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';

const steps = [
  {
    icon: <FaHandshake />,
    iconColor: '#2563eb',
    title: 'Welcome to Pop1!',
    description: "You're now part of a community of trading card collectors. Here's a quick look at what you can do.",
  },
  {
    icon: <FaNewspaper />,
    iconColor: '#0ea5e9',
    title: 'Your Feed',
    description: 'Browse posts and cards from collectors you follow. The more you follow, the richer your feed gets.',
  },
  {
    icon: <FaStar />,
    iconColor: '#8b3d3a',
    title: 'Suggested',
    description: 'Use the Suggested tab on your Feed to find new collectors and cards recommended for you.',
  },
  {
    icon: <FaLayerGroup />,
    iconColor: '#059669',
    title: 'My Cards',
    description: 'Add your cards to build your collection. Showcase your best pulls and track what you own.',
  },
  {
    icon: <FaStore />,
    iconColor: '#d97706',
    title: 'Marketplace',
    description: 'Buy and sell cards with other collectors. List a card for sale or browse what others have posted.',
  },
  {
    icon: <FaUser />,
    iconColor: '#7c3aed',
    title: 'Your Profile',
    description: 'Complete your profile to attract followers and build your reputation as a collector.',
  },
];

const getTutorialKey = (userId) => `pop1_tutorial_seen_${userId}`;

const TutorialOverlay = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (user?.id && !localStorage.getItem(getTutorialKey(user.id))) {
      setStep(0);
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [user?.id]);

  // Allow external trigger (e.g. navbar ? button) to re-show the tutorial
  useEffect(() => {
    const handleShow = () => {
      setStep(0);
      setVisible(true);
    };
    window.addEventListener('showTutorial', handleShow);
    return () => window.removeEventListener('showTutorial', handleShow);
  }, []);

  const dismiss = () => {
    if (user?.id) {
      localStorage.setItem(getTutorialKey(user.id), 'true');
    }
    setVisible(false);
  };

  const next = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (step < steps.length - 1) {
      setStep(s => s + 1);
    } else {
      dismiss();
    }
  };

  const skip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dismiss();
  };

  if (!visible) return null;

  const current = steps[step];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.3)',
        padding: '16px',
        boxSizing: 'border-box',
        touchAction: 'none',
        overscrollBehavior: 'none',
      }}
      onClick={skip}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Tutorial step ${step + 1} of ${steps.length}: ${current.title}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '320px',
          maxWidth: '100%',
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)',
          padding: '20px',
          border: '1px solid rgba(37, 99, 235, 0.12)',
          fontFamily: 'inherit',
        }}
      >
        {/* Step dots */}
        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', marginBottom: '14px' }}>
          {steps.map((_, i) => (
            <div
              key={i}
              style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: i === step ? '#2563eb' : '#e2e8f0',
                transition: 'background 0.2s',
              }}
            />
          ))}
        </div>

        {/* Icon + title */}
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: `${current.iconColor}18`,
            color: current.iconColor,
            fontSize: '22px',
          }}>
            {current.icon}
          </div>
          <h3 style={{ margin: '8px 0 0', fontSize: '15px', fontWeight: '700', color: '#1a202c' }}>
            {current.title}
          </h3>
        </div>

        {/* Description */}
        <p style={{
          textAlign: 'center',
          color: '#4a5568',
          fontSize: '13px',
          margin: '8px 0 16px',
          lineHeight: 1.5,
        }}>
          {current.description}
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={skip}
            type="button"
            style={{
              flex: 1,
              padding: '9px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              background: '#fff',
              cursor: 'pointer',
              color: '#718096',
              fontSize: '13px',
              fontFamily: 'inherit',
              WebkitAppearance: 'none',
            }}
          >
            Skip
          </button>
          <button
            onClick={next}
            type="button"
            style={{
              flex: 2,
              padding: '9px',
              border: 'none',
              borderRadius: '8px',
              background: '#2563eb',
              cursor: 'pointer',
              color: '#fff',
              fontSize: '13px',
              fontWeight: '600',
              fontFamily: 'inherit',
              WebkitAppearance: 'none',
            }}
          >
            {step === steps.length - 1 ? "Let's go!" : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
